import { z, isoZ } from 'my-zod'
import { attachmentModel } from '../../utilities/attachment'
import { TeamsEnum } from '../../utilities/user/teams-enum'

export const AccountCaseLifecycleStatusEnum = z
  .enum(['Draft', 'AssignedToTeam', 'InProgress', 'Resolved', 'Closed'])
  .setEnumLabels({
    Draft: 'Draft',
    AssignedToTeam: 'Assigned to Team',
    InProgress: 'In Progress',
    Resolved: 'Resolved',
    Closed: 'Closed'
  })

export type AccountCaseLifecycleStatusEnum = z.infer<typeof AccountCaseLifecycleStatusEnum>

export const AccountCaseSupportTypeEnum = z
  .enum([
    'AMVehicleRelatedInquiry',
    'AMOutfitterInquiry',
    'AMOutfitterSatisfaction',
    'AMGeneralOperations',
    'FleetLogistics',
    'FleetSIOP',
    'FleetUpfit',
    'FleetRegistration',
    'FleetDisposition',
    'ServiceOpsDamageWaiver',
    'ServiceOpsProgramAgreement',
    'ServiceOpsOutfitterPerformance',
    'ServiceOpsWaiverDocuments',
    'ServiceOpsInsurancePolicyAndClaims',
    'ServiceSupportVehicleRepair',
    'ServiceSupportVehicleMods',
    'ServiceSupportSafetyBulletin',
    'QuoteInquiry'
  ])
  .setEnumLabels({
    AMVehicleRelatedInquiry: 'Vehicle Related Inquiry',
    AMOutfitterInquiry: 'Outfitter Inquiry',
    AMOutfitterSatisfaction: 'Outfitter Satisfaction',
    AMGeneralOperations: 'General Operations',
    FleetLogistics: 'Logistics',
    FleetSIOP: 'SIOP: (Demand/Orders)',
    FleetUpfit: 'Upfit',
    FleetRegistration: 'Registration/Title',
    FleetDisposition: 'Disposition',
    ServiceOpsDamageWaiver: 'Damage Waiver',
    ServiceOpsProgramAgreement: 'Program Agreement',
    ServiceOpsOutfitterPerformance: 'Outfitter Performance',
    ServiceOpsWaiverDocuments: 'Waiver Documents',
    ServiceOpsInsurancePolicyAndClaims: 'Insurance Policy & Claims',
    ServiceSupportVehicleRepair: 'Vehicle Repair/Service',
    ServiceSupportVehicleMods: 'Vehicle Modifications/Accessories',
    ServiceSupportSafetyBulletin: 'Service/Safety Bulletin',
    QuoteInquiry: 'Quote Inquiry'
  })

export type AccountCaseSupportTypeEnum = z.infer<typeof AccountCaseSupportTypeEnum>

export const AccountCasePriorityEnum = z.enum(['Normal', 'High', 'Urgent'])

export const accountCaseModel = z
  .object({
    shortId: z.string(),
    outfitterId: z.number().min(1, 'Required').setLabel('Outfitter Name'),
    ownerUserId: z.number().min(1, 'Required').setLabel('Case Owner'),
    createdUserId: z.number().min(1, 'Required').setLabel('Created By'),
    subject: z.string().nonempty().setLabel('Subject'),
    notes: z.string(),
    lifecycleStatus: AccountCaseLifecycleStatusEnum.setLabel('Status'),
    supportType: AccountCaseSupportTypeEnum.nullable(),
    priority: AccountCasePriorityEnum.nullable().setLabel('Priority Level'),
    assignedTeam: TeamsEnum.nullable().setLabel('Team Assigned'),
    contactId: z.string().uuid().nullable().setLabel('Outfitter Point of Contact'),
    assignedUserId: z.number().nullable().setLabel('Assigned To'),
    ticketLink: z.string().nullable().setLabel('Zendesk Ticket Link'),
    serviceBulletinId: z.string().uuid().nullable().setLabel('Service Bulletin Link'),
    caseId: z.string().nullable().setLabel('Case Link'),
    quoteId: z.string().nullable().setLabel('Quote Link'),
    dateOfInquiry: isoZ.date().nullable().setLabel('Date of Question'),
    resolutionDate: isoZ.date().nullable(),
    description: z.string().nullable(),
    recommendation: z.string().nullable(),
    resolution: z.string().nullable(),
    attachments: attachmentModel.array(),
    lastModifiedByUserId: z.number().setLabel('Last Modified By'),
    completionDate: isoZ.instant().nullable().setLabel('Closed Date'),
    createdDate: isoZ.instant(),
    deletedDate: isoZ.instant().nullable(),
    lastModifiedDate: isoZ.instant()
  })
  .setCFV({
    supportTypeRequired(val) {
      if (!val.supportType) {
        return {
          message: 'Required',
          path: ['supportType']
        }
      }
    },
    priorityRequired(val) {
      if (!val.priority) {
        return {
          message: 'Required',
          path: ['priority']
        }
      }
    },
    assignedTeamRequired(val) {
      if (!val.assignedTeam) {
        return {
          message: 'Required',
          path: ['assignedTeam']
        }
      }
    }
  })

export type accountCaseModel = z.infer<typeof accountCaseModel>
